import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SeatsWon = () => {
  const { t, language } = useLanguage();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/seats-won`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className={`text-center py-12 ${language === 'mr' ? 'marathi-text' : ''}`}>{t('loading')}</div>;
  }

  const chartData = data.map(item => ({
    party: item.party_name,
    seats: item.total_seats,
    sc: item.sc_seats,
    st: item.st_seats,
    open: item.open_seats
  }));

  return (
    <div className="space-y-6" data-testid="seats-won-page">
      <div>
        <h1 className={`text-3xl font-bold text-primary mb-2 ${language === 'mr' ? 'marathi-text' : ''}`}>
          {t('seatsWon')}
        </h1>
        <p className={`text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
          {language === 'en' ? 'Statement 9: Party-wise seat distribution' : 'विधान 9: पक्षानुसार जागांचे वितरण'}
        </p>
      </div>

      {/* Bar Chart */}
      {chartData.length > 0 && (
        <Card data-testid="seats-chart">
          <CardHeader>
            <CardTitle className={language === 'mr' ? 'marathi-text' : ''}>
              {language === 'en' ? 'Seats Distribution Chart' : 'जागा वितरण चार्ट'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="party" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="seats" fill="#003366" name={language === 'en' ? 'Total Seats' : 'एकूण जागा'} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className={language === 'mr' ? 'marathi-text' : ''}>
            {language === 'en' ? 'Detailed Breakdown' : 'तपशीलवार माहिती'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('party')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{t('totalSeats')}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'SC Seats' : 'SC जागा'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'ST Seats' : 'ST जागा'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Open Seats' : 'मोकली जागा'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'Open Women' : 'महिला मोकली'}</TableHead>
                  <TableHead className={language === 'mr' ? 'marathi-text' : ''}>{language === 'en' ? 'BCC Women' : 'BCC महिला'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.length > 0 ? (
                  data.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-bold text-primary">{item.party_name}</TableCell>
                      <TableCell className="font-semibold text-lg">{item.total_seats}</TableCell>
                      <TableCell>{item.sc_seats}</TableCell>
                      <TableCell>{item.st_seats}</TableCell>
                      <TableCell>{item.open_seats}</TableCell>
                      <TableCell>{item.open_w_seats}</TableCell>
                      <TableCell>{item.bcc_w_seats}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className={language === 'mr' ? 'marathi-text' : ''}>{t('noData')}</div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SeatsWon;